import React, { useState, useEffect, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  TrendingUp, TrendingDown, Calculator, Star, Info, Plus, X, Search,
  RefreshCw, PieChart as PieChartIcon, Landmark, ShieldCheck,
  Zap, ExternalLink, ArrowUpRight, Gauge, Layers, Sparkles, MousePointer2,
  ChevronRight, ArrowRight
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';
import { financialDataAPI, portfolioAPI } from '../services/apiServices';
import { toast } from '../hooks/use-toast';

// === NEBULA THEME CONSTANTS ===
const COLORS = {
  gold: { stop1: '#FACC15', stop2: '#CA8A04' },
  silver: { stop1: '#CBD5E1', stop2: '#94A3B8' },
  gas: { stop1: '#22D3EE', stop2: '#0891B2' },
  oil: { stop1: '#F97316', stop2: '#EA580C' },
  btc: { stop1: '#F59E0B', stop2: '#B45309' },
  eth: { stop1: '#6366F1', stop2: '#4338CA' },
  sol: { stop1: '#14F195', stop2: '#10B981' },
  sip: ['#F59E0B', '#1E293B'] // Amber and Slate
};

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(255,255,255,0.06)" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        background: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

// === MANUAL TOP BANKS FD DATA ===
const FIXED_DEPOSITS = [
  { id: 1, bankName: 'HDFC Bank', interestRate: 6.45, tenure: '2 - 3 Years', minAmount: '₹5,000', link: 'https://www.hdfc.bank.in/fixed-deposit/fd-interest-rate' },
  { id: 2, bankName: 'ICICI Bank', interestRate: 6.45, tenure: '2 - 3 Years', minAmount: '₹10,000', link: 'https://www.icici.bank.in/personal-banking/deposits/fixed-deposit' },
  { id: 3, bankName: 'SBI', interestRate: 6.45, tenure: '2 - 3 Years', minAmount: '₹1,000', link: 'https://sbi.bank.in/web/personal-banking/investments-deposits/deposits/fixed-deposit' },
  { id: 4, bankName: 'Axis Bank', interestRate: 6.45, tenure: '2 - 3 Years', minAmount: '₹5,000', link: 'https://www.axis.bank.in/deposits/fixed-deposits' },
  { id: 5, bankName: 'Kotak Mahindra', interestRate: 6.70, tenure: '2 - 3 Years', minAmount: '₹5,000', link: 'https://www.kotak.bank.in/en/personal-banking/deposits.html' },
  { id: 6, bankName: 'IndusInd Bank', interestRate: 6.90, tenure: '2 - 3 Years', minAmount: '₹10,000', link: 'https://www.indusind.bank.in/in/en/personal/deposits/fixed-deposit.html?utm_source=website&utm_medium=header_nav&utm_campaign=accounts_deposits-fixed_deposit' },
  { id: 7, bankName: 'IDFC First Bank', interestRate: 7.00, tenure: '2 - 3 Years', minAmount: '₹10,000', link: 'https://www.idfcfirst.bank.in/personal-banking/deposits/fixed-deposit' },
  { id: 8, bankName: 'Canara Bank', interestRate: 6.25, tenure: '2 - 3 Years', minAmount: '₹1,000', link: 'https://www.canarabank.bank.in/fixed-deposit' },
  { id: 9, bankName: 'Bank of Baroda', interestRate: 6.25, tenure: '2 - 3 Years', minAmount: '₹5,000', link: 'https://bankofbaroda.bank.in/personal-banking/accounts/term-deposit' },
  { id: 10, bankName: 'Union Bank of India', interestRate: 6.25, tenure: '2 - 3 Years', minAmount: '₹5,000', link: 'https://www.unionbankofindia.bank.in/en/details/fixed-deposit-receipt-fdr' },
];

const Investments = () => {
  const [trackedFunds, setTrackedFunds] = useState(() => {
    const saved = localStorage.getItem('tracked_mf_funds');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // SIP Calculator State - Cleared by default
  const [sipCalculator, setSipCalculator] = useState({
    monthlyInvestment: '',
    timePeriod: '',
    expectedReturn: ''
  });
  const [sipResult, setSipResult] = useState(null);
  const [animatedSipResult, setAnimatedSipResult] = useState(null);

  // Persistence Effect
  useEffect(() => {
    localStorage.setItem('tracked_mf_funds', JSON.stringify(trackedFunds));
  }, [trackedFunds]);

  // SIP Odometer Animation
  useEffect(() => {
    if (sipResult) {
      let startTime = null;
      const duration = 1500;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 5); // Smooth Quintic Ease
        const actualPercent = ((parseFloat(sipResult.returns) / parseFloat(sipResult.invested)) * 100);
        setAnimatedSipResult({
          totalValue: (parseFloat(sipResult.totalValue) * ease).toFixed(0),
          invested: (parseFloat(sipResult.invested) * ease).toFixed(0),
          returns: (parseFloat(sipResult.returns) * ease).toFixed(0),
          percentCompounded: (progress * actualPercent).toFixed(1)
        });
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [sipResult]);

  // Discover Funds Logic
  const [discoverFunds, setDiscoverFunds] = useState([]);
  const [discoverSearch, setDiscoverSearch] = useState('');
  const [loadingDiscover, setLoadingDiscover] = useState(false);

  // Portfolio Modal State
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [selectedForPortfolio, setSelectedForPortfolio] = useState(null);
  const [portfolioForm, setPortfolioForm] = useState({
    quantity: '1',
    buyDate: new Date().toISOString().split('T')[0],
    buyPrice: ''
  });

  // Markets State
  const [marketCategory, setMarketCategory] = useState('commodities');
  const [marketData, setMarketData] = useState({
    gold: null, silver: null, natural_gas: null, btc: null, eth: null, crude_oil: null, currencies: [], trending: [],
    history: { gold: [], silver: [], gas: [], btc: [], eth: [], crude: [] }
  });

  // Dynamic Exchange Rate
  const [usdInr, setUsdInr] = useState(86.4);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        // Fetch Exchange Rate first or use default
        const rate = await financialDataAPI.getExchangeRate().catch(() => 86.4);
        setUsdInr(rate);

        // Fetch Prices (Independent try-catches for robustness)
        const fetchPrices = async () => {
          try {
            const metals = await financialDataAPI.getMetalsPrices();
            const btc = await financialDataAPI.getCryptoPrice('bitcoin');
            const eth = await financialDataAPI.getCryptoPrice('ethereum');
            const trending = await financialDataAPI.getTrendingCrypto();
            const currenciesResult = await financialDataAPI.getCurrencyPairs();

            setMarketData(prev => {
              const updatedCurrencies = currenciesResult.map(curr => {
                const prevCurr = prev.currencies.find(p => p.symbol === curr.symbol);
                const trend = prevCurr ? (curr.price > prevCurr.price ? 'up' : curr.price < prevCurr.price ? 'down' : prevCurr.trend || 'up') : 'up';
                return { ...curr, trend };
              });

              return {
                ...prev,
                gold: metals.gold,
                silver: metals.silver,
                natural_gas: metals.natural_gas,
                crude_oil: metals.crude_oil,
                btc,
                eth,
                trending,
                currencies: updatedCurrencies
              };
            });

            return {
              gold: metals.gold?.price,
              silver: metals.silver?.price,
              gas: metals.natural_gas?.price,
              crude: metals.crude_oil?.price
            };
          } catch (e) {
            console.error("Price fetch failed", e);
            return null;
          }
        };

        // Fetch History (Only on Mount or occasionally)
        const fetchHistory = async (livePrices = {}) => {
          try {
            const [gc, si, ng, cl, b, e] = await Promise.all([
              financialDataAPI.getHistoricalCommodity('GCUSD', livePrices.gold).catch(() => []),
              financialDataAPI.getHistoricalCommodity('SIUSD', livePrices.silver).catch(() => []),
              financialDataAPI.getHistoricalCommodity('NGUSD', livePrices.gas).catch(() => []),
              financialDataAPI.getHistoricalCommodity('CLUSD', livePrices.crude).catch(() => []),
              financialDataAPI.getHistoricalCrypto('bitcoin').catch(() => []),
              financialDataAPI.getHistoricalCrypto('ethereum').catch(() => [])
            ]);
            setMarketData(prev => ({
              ...prev,
              history: { gold: gc, silver: si, gas: ng, crude: cl, btc: b, eth: e }
            }));
          } catch (e) {
            console.error("History fetch failed", e);
          }
        };

        const live = await fetchPrices();
        await fetchHistory(live || {});
      } catch (err) {
        console.error("Market data sync error", err);
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 60000); // Live sync every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadInitialFunds = async () => {
      setLoadingDiscover(true);
      const codes = [
        '120503', '125497', '122639', '119551', '120594', '118989', '120716', '100119',
        '120828', '118671', '120464', '120505', '119598', '120152', '118834', '119770',
        '120165', '118814', '119564', '120146', '118825', '119615', '120155', '118778',
        '119830', '120224', '118845', '119630', '118698', '148962', '120441', '100033',
        '149062', '149162', '149262', '149362', '149462', '149562', '149662', '149762',
        '149862', '149962', '150062', '150162', '150262', '150362', '150462', '150562'
      ];
      try {
        const fundPromises = codes.map(code => financialDataAPI.getMFDetails(code).catch(() => null));
        const funds = (await Promise.all(fundPromises)).filter(f => f !== null);
        const currentTrackedCodes = trackedFunds.map(f => f.schemeCode);
        setDiscoverFunds(funds.filter(f => !currentTrackedCodes.includes(f.schemeCode)));
      } catch (error) { }
      setLoadingDiscover(false);
    };
    loadInitialFunds();
  }, [trackedFunds.length === 0]);

  // Debounced Suggestion Logic
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchCode.length > 2) {
        setSearching(true);
        const results = await financialDataAPI.searchMF(searchCode);
        if (results) setSuggestions(results.slice(0, 5));
        setSearching(false);
      } else {
        setSuggestions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchCode]);

  const addFundToTracking = (fund) => {
    if (trackedFunds.find(f => f.schemeCode === fund.schemeCode)) return;
    setTrackedFunds(prev => [fund, ...prev]);
    setDiscoverFunds(prev => prev.filter(f => f.schemeCode !== fund.schemeCode));
    toast({ title: 'System Security', description: `${fund.schemeName} synchronized for tracking.` });
  };

  const removeFund = (code) => {
    setTrackedFunds(prev => prev.filter(f => f.schemeCode !== code));
  };

  const calculateSIP = (e) => {
    if (e) e.preventDefault();
    const P = parseFloat(sipCalculator.monthlyInvestment);
    const y = parseFloat(sipCalculator.timePeriod);
    const r_annual = parseFloat(sipCalculator.expectedReturn) / 100;
    if (!P || !y || !r_annual) {
      toast({ title: 'Incomplete Data', description: 'Please fill all parameters.' });
      return;
    }
    const i = r_annual / 12;
    const n = y * 12;
    const maturityValue = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const totalInvested = P * n;
    setSipResult({ totalValue: maturityValue.toFixed(2), invested: totalInvested.toFixed(2), returns: (maturityValue - totalInvested).toFixed(2) });
  };

  const openPortfolioModal = (fund) => {
    setSelectedForPortfolio(fund);
    setPortfolioForm({ quantity: '1', buyDate: new Date().toISOString().split('T')[0], buyPrice: fund.currentNav || '' });
    setShowPortfolioModal(true);
  };

  const handlePortfolioSubmit = async () => {
    try {
      await portfolioAPI.addAsset({
        asset_type: 'Mutual Fund', asset_name: selectedForPortfolio.schemeName, ticker: selectedForPortfolio.schemeCode,
        quantity: parseFloat(portfolioForm.quantity), buy_price: parseFloat(portfolioForm.buyPrice), current_price: selectedForPortfolio.currentNav,
        buy_date: portfolioForm.buyDate, description: selectedForPortfolio.fundHouse
      });
      toast({ title: 'Portfolio Synced', description: 'Asset induction successful.' });
      setShowPortfolioModal(false);
    } catch (e) { toast({ title: 'Error', description: 'Failed to induct asset.' }); }
  };

  const handleGlobalSearch = async () => {
    if (!searchCode) return;
    setSearching(true);
    const res = await financialDataAPI.getMFDetails(searchCode);
    if (res) {
      setSearchResult(res);
      setSuggestions([]);
    } else {
      toast({ title: 'Not Found', description: 'Registry mismatch for specific ID.' });
    }
    setSearching(false);
  };

  const renderMiniChart = (data, colorStart, colorEnd, currency = '₹', conversion = 1) => (
    <div className="h-20 w-full mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`grad-${colorStart}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colorStart} stopOpacity={0.3} />
              <stop offset="95%" stopColor={colorEnd} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={['auto', 'auto']} />
          <Area type="monotone" dataKey="val" stroke={colorStart} strokeWidth={2} fill={`url(#grad-${colorStart})`} dot={false} isAnimationActive={true} />
          <Tooltip content={<CustomTooltip color={colorStart} currency={currency} conversion={conversion} />} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  const CustomTooltip = ({ active, payload, color, currency, conversion }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 p-2 rounded-lg shadow-2xl">
          <p className="text-[10px] font-black text-white" style={{ color }}>
            {currency}{(payload[0].value * conversion).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#020617] text-slate-100 p-4 md:p-8 font-sans selection:bg-cyan-500/30 overflow-x-hidden">

        {/* Header */}
        <div className="mb-8 flex flex-col items-start animate-in fade-in slide-in-from-left-6 duration-1000">
          <div className="mb-3 flex items-center gap-2 bg-cyan-500/5 px-2 py-0.5 rounded-full border border-cyan-500/10">
            <Sparkles size={10} className="text-cyan-400" />
            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-cyan-400">Institutional Wealth Console</span>
          </div>
          <h1 className="text-xl md:text-3xl font-black tracking-tighter text-white uppercase leading-none mb-3">
            INVESTMENT <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-500">DECK</span>
          </h1>
          <p className="text-slate-500 text-[13px] max-w-2xl font-medium leading-relaxed opacity-80">
            Professional-grade monitoring across global indices, mutual funds, and sovereign debt instruments.
          </p>
        </div>

        <Tabs defaultValue="mutual-funds" className="space-y-10">
          <TabsList className="bg-white/5 backdrop-blur-2xl p-1.5 border border-white/10 rounded-xl flex w-fit">
            {['mutual-funds', 'fixed-deposits', 'calculator', 'markets'].map(tab => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="data-[state=active]:bg-white/10 data-[state=active]:text-white rounded-lg text-slate-500 px-6 py-2 text-[11px] font-black uppercase tracking-widest transition-all"
              >
                {tab === 'calculator' ? 'SIP Calculator' : tab.replace('-', ' ')}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* === MUTUAL FUNDS === */}
          <TabsContent value="mutual-funds" className="space-y-12">
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-b border-white/5 pb-6 animate-in fade-in slide-in-from-left-6 duration-1000">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-cyan-500/10 rounded-xl text-cyan-400 border border-cyan-500/20"><Layers size={24} /></div>
                  <div>
                    <h3 className="text-2xl font-black text-white tracking-tight">Active Portfolios</h3>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-0.5">Real-time Asset Surveillance</p>
                  </div>
                </div>
                <Button onClick={() => setShowAddModal(true)} className="bg-white/5 hover:bg-white/10 text-white/50 hover:text-white border border-white/10 rounded-xl px-5 h-11 text-[9px] font-black uppercase tracking-[0.1em] shadow-xl flex items-center gap-2 transition-all">
                  <Search size={14} className="opacity-50" />
                  Search for more mutual funds / Add via scheme code
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {trackedFunds.map((fund) => (
                  <TiltCard key={fund.schemeCode} depth={10} className="bg-[#0F172A]/50 border border-white/5 rounded-3xl p-6 flex flex-col hover:border-cyan-500/50 shadow-2xl">
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-6">
                        <span className="text-[9px] font-black text-cyan-500 bg-cyan-500/10 px-3 py-1 rounded-lg border border-cyan-500/20">ID: {fund.schemeCode}</span>
                        <button onClick={() => removeFund(fund.schemeCode)} className="text-slate-700 hover:text-red-500 transition-all hover:scale-110"><X size={18} /></button>
                      </div>
                      <h4 className="text-[13px] font-black text-white leading-tight mb-6 line-clamp-3 min-h-[2.5rem] opacity-90 uppercase tracking-tight">{fund.schemeName}</h4>

                      <div className="space-y-6">
                        <div className="bg-black/40 p-4 rounded-2xl border border-white/10">
                          <p className="text-[8px] text-slate-500 font-black uppercase tracking-[0.15em] mb-1.5">Live Valuation</p>
                          <div className="text-lg font-black text-white">₹{fund.currentNav}</div>
                          <p className="text-[7px] text-slate-600 font-mono mt-1 uppercase tracking-widest">{fund.date}</p>
                        </div>

                        {fund.change !== undefined && (
                          <div className={`p-2.5 rounded-xl flex items-center justify-between border ${fund.change >= 0 ? 'bg-emerald-500/5 border-emerald-500/10 text-emerald-400' : 'bg-rose-500/5 border-rose-500/10 text-rose-400'}`}>
                            <div className="flex items-center gap-1">
                              {fund.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                              <span className="text-[8px] font-black uppercase tracking-widest">Growth</span>
                            </div>
                            <span className="text-xs font-black">{fund.changePercent?.toFixed(2)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button onClick={() => openPortfolioModal(fund)} className="w-full mt-8 h-9 bg-cyan-600 text-white hover:bg-cyan-500 text-[9px] font-black uppercase rounded-lg tracking-[0.1em] shadow-lg shadow-cyan-900/40">Add to Portfolio</Button>
                  </TiltCard>
                ))}
              </div>
            </div>

            <div className="space-y-8">
              <div className="flex flex-col md:flex-row justify-between items-end gap-6 animate-in fade-in slide-in-from-left-6 duration-1000">
                <div>
                  <h3 className="text-2xl font-black text-white tracking-tight">Market Discovery</h3>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-0.5">Global Selection Engine</p>
                </div>
                <div className="w-full md:w-80 group">
                  <p className="text-[9px] font-black text-cyan-500 mb-1.5 ml-1 uppercase flex items-center gap-2"><Info size={10} /> Search indices or fund houses</p>
                  <div className="flex bg-white/5 p-1.5 rounded-2xl border border-white/10 group-focus-within:border-cyan-500/50 transition-all shadow-xl items-center">
                    <Search size={16} className="ml-3 text-white/30" />
                    <Input placeholder="Axis, HDFC, Quant..." value={discoverSearch} onChange={(e) => setDiscoverSearch(e.target.value)} className="bg-transparent border-0 text-xs h-10 focus:ring-0 text-white/60 placeholder:text-white/20 flex-1" />
                    <Button onClick={async () => { setLoadingDiscover(true); const data = await financialDataAPI.searchMF(discoverSearch); if (data) setDiscoverFunds(data.slice(0, 30)); setLoadingDiscover(false); }} className="h-10 w-10 bg-cyan-600 rounded-xl p-0 hover:bg-cyan-500 shadow-lg shadow-cyan-900/20"><Search size={16} /></Button>
                  </div>
                </div>
              </div>

              {loadingDiscover ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[...Array(8)].map((_, i) => <div key={i} className="h-64 bg-white/5 animate-pulse rounded-3xl border border-white/10"></div>)}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-[600px] overflow-y-auto pr-3 custom-scrollbar">
                  {discoverFunds.map(fund => (
                    <TiltCard key={fund.schemeCode} depth={10} className="bg-[#0F172A]/30 border border-white/5 rounded-3xl p-6 h-72 flex flex-col hover:border-cyan-500/40 shadow-xl">
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-4">
                          <span className="text-[9px] font-black text-slate-500 bg-white/5 px-3 py-1 rounded-lg border border-white/10">CODE {fund.schemeCode}</span>
                          <span className="text-[8px] font-black text-cyan-400 bg-cyan-400/5 px-2 py-0.5 rounded-full border border-cyan-400/10 uppercase tracking-widest">{fund.schemeCategory?.split(' ')[0] || 'EQUITY'}</span>
                        </div>
                        <h4 className="text-[13px] font-black text-slate-200 line-clamp-3 leading-tight mb-3 opacity-90 uppercase tracking-tight">{fund.schemeName}</h4>
                        <p className="text-[8px] text-slate-500 font-black uppercase tracking-[0.2em]">{fund.fundHouse?.slice(0, 30)}</p>
                      </div>
                      <Button onClick={() => addFundToTracking(fund)} className="w-full mt-4 h-9 bg-white/5 hover:bg-cyan-600 text-[9px] font-black uppercase text-slate-400 hover:text-white rounded-lg border border-white/10 transition-all shadow-lg shadow-black/40">Track Performance</Button>
                    </TiltCard>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* === FIXED DEPOSITS === */}
          <TabsContent value="fixed-deposits" className="space-y-10">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 p-8 rounded-[2.5rem] bg-gradient-to-br from-indigo-950/40 via-blue-950/20 to-transparent border border-indigo-500/10 flex items-center gap-6 relative overflow-hidden shadow-2xl animate-in fade-in slide-in-from-left-6 duration-1000">
                <div className="absolute -top-32 -right-32 w-80 h-80 bg-indigo-500/10 rounded-full blur-[100px]"></div>
                <div className="p-4 bg-indigo-500/10 rounded-2xl text-indigo-400 border border-indigo-500/20 shadow-2xl"><Landmark size={32} /></div>
                <div>
                  <h3 className="text-3xl font-black text-white uppercase tracking-wider">Sovereign Debt Desk</h3>
                  <p className="text-indigo-400/60 text-xs font-black uppercase tracking-[0.2em] mt-1.5">Institutional Grade Deposits</p>
                </div>
              </div>

              <div className="p-6 rounded-[2rem] bg-white/5 border border-white/5 backdrop-blur-xl flex flex-col gap-3 min-w-[300px]">
                <div className="flex items-center gap-3">
                  <Info size={14} className="text-indigo-400" />
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Tenure: 2 - 3 Years Average</p>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck size={14} className="text-indigo-400" />
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Cap: &lt; 3 Crores | General Category</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...FIXED_DEPOSITS].sort((a, b) => b.interestRate - a.interestRate).map((fd) => (
                <TiltCard key={fd.id} className="bg-[#0F172A] border border-indigo-500/10 rounded-3xl p-7 group hover:border-indigo-500/50 shadow-2xl">
                  <h3 className="text-base font-black text-white hover:text-indigo-400 transition-colors uppercase mb-4 h-6 line-clamp-1">{fd.bankName}</h3>
                  <div className="text-center p-4 bg-black/40 rounded-2xl border border-indigo-900/30 mb-6 group-hover:bg-indigo-950/40 transition-all shadow-inner">
                    <p className="text-2xl font-black text-indigo-400 mb-1">{fd.interestRate}%</p>
                    <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">Annual Yield Profile</p>
                  </div>
                  <div className="space-y-3 mb-8">
                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest"><span className="text-slate-500">Lock Window</span><span className="text-slate-200">{fd.tenure}</span></div>
                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest"><span className="text-slate-500">Initial Cap</span><span className="text-slate-200">{fd.minAmount}</span></div>
                  </div>
                  <a href={fd.link} target="_blank" rel="noopener noreferrer" className="w-full">
                    <Button className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-[10px] font-black uppercase rounded-xl gap-2 tracking-[0.1em] shadow-xl shadow-indigo-900/30">
                      Apply Officially <ArrowUpRight size={16} />
                    </Button>
                  </a>
                </TiltCard>
              ))}
            </div>
          </TabsContent>

          {/* === SIP CALCULATOR === */}
          <TabsContent value="calculator" className="space-y-12">
            <div className="grid lg:grid-cols-12 gap-8 items-stretch">
              <div className="lg:col-span-4">
                <TiltCard className="p-8 bg-[#0C0A09] rounded-3xl border border-amber-900/30 shadow-3xl h-full flex flex-col relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 blur-3xl rounded-full"></div>
                  <div className="flex items-center gap-4 mb-8 animate-in fade-in slide-in-from-left-6 duration-1000">
                    <div className="p-3 bg-amber-500/10 rounded-2xl text-amber-500 border border-amber-500/20 shadow-xl"><Calculator size={28} /></div>
                    <h2 className="text-2xl font-black text-white tracking-tight">SIP Forecaster</h2>
                  </div>

                  <form onSubmit={calculateSIP} className="space-y-6 flex-1">
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500/70 ml-1">Monthly Capital (₹)</Label>
                      <Input className="bg-black/50 border-amber-900/40 text-amber-400/80 h-12 rounded-xl px-4 font-black focus:ring-amber-500 text-xl shadow-inner placeholder:text-white/10 placeholder:font-normal" placeholder="5,000" type="number" value={sipCalculator.monthlyInvestment} onChange={e => setSipCalculator({ ...sipCalculator, monthlyInvestment: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500/70 ml-1">Expected ROI (%)</Label>
                      <Input className="bg-black/50 border-amber-900/40 text-amber-400/80 h-12 rounded-xl px-4 font-black focus:ring-amber-500 text-xl shadow-inner placeholder:text-white/10 placeholder:font-normal" placeholder="12%" type="number" value={sipCalculator.expectedReturn} onChange={e => setSipCalculator({ ...sipCalculator, expectedReturn: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500/70 ml-1">Duration (Yrs)</Label>
                      <Input className="bg-black/50 border-amber-900/40 text-amber-400/80 h-12 rounded-xl px-4 font-black focus:ring-amber-500 text-xl shadow-inner placeholder:text-white/10 placeholder:font-normal" placeholder="10 yrs" type="number" value={sipCalculator.timePeriod} onChange={e => setSipCalculator({ ...sipCalculator, timePeriod: e.target.value })} />
                    </div>
                    <Button type="submit" className="w-full h-12 text-[11px] font-black uppercase tracking-[0.3em] bg-amber-600 hover:bg-amber-500 text-black rounded-xl transition-all shadow-2xl shadow-amber-900/40 mt-auto">Execute Probability Model</Button>
                  </form>
                </TiltCard>
              </div>

              <div className="lg:col-span-8 flex flex-col gap-6">
                {animatedSipResult ? (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-6 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-2xl shadow-2xl group hover:border-white/20 transition-all">
                        <p className="text-[10px] font-black uppercase text-slate-500 mb-2 tracking-widest">Capital Invested</p>
                        <p className="text-2xl font-black text-white tracking-tight">₹{Number(animatedSipResult.invested).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="p-6 rounded-3xl bg-emerald-500/5 border border-emerald-500/10 backdrop-blur-2xl shadow-2xl group hover:border-emerald-500/20 transition-all">
                        <p className="text-[10px] font-black uppercase text-emerald-500/60 mb-2 tracking-widest">Earnings Yield</p>
                        <p className="text-2xl font-black text-emerald-400 tracking-tight">+₹{Number(animatedSipResult.returns).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="p-6 rounded-3xl bg-amber-500/5 border border-amber-500/10 backdrop-blur-2xl shadow-2xl group hover:border-amber-500/20 transition-all">
                        <p className="text-[10px] font-black uppercase text-amber-500/60 mb-2 tracking-widest">Maturity Wealth</p>
                        <p className="text-2xl font-black text-amber-500 tracking-tight">₹{Number(animatedSipResult.totalValue).toLocaleString('en-IN')}</p>
                      </div>
                    </div>

                    <div className="flex-1 bg-[#0F172A]/40 border border-white/5 rounded-[2.5rem] p-8 flex flex-col md:flex-row items-center gap-12 shadow-3xl relative overflow-hidden group">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent pointer-events-none"></div>
                      <div className="w-[280px] h-[280px] shrink-0 relative flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <defs>
                              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                              </filter>
                            </defs>
                            <Pie
                              data={[
                                { name: 'Core Capital', value: parseFloat(animatedSipResult.invested) },
                                { name: 'Compounded Returns', value: parseFloat(animatedSipResult.returns) }
                              ]}
                              innerRadius={70} outerRadius={110} paddingAngle={8} dataKey="value" stroke="none"
                              animationBegin={0} animationDuration={1500} animationEasing="ease-out"
                            >
                              <Cell fill="#1e293b" />
                              <Cell fill="#F59E0B" filter="url(#glow)" />
                            </Pie>
                            <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff', fontSize: '12px', fontWeight: 'bold' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute flex flex-col items-center">
                          <span className="text-amber-500 font-black text-3xl leading-none">{animatedSipResult.percentCompounded}%</span>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Progressive</span>
                        </div>
                      </div>
                      <div className="flex-1 space-y-8 z-10">
                        <div className="space-y-4">
                          <h4 className="text-4xl font-black text-white uppercase tracking-tighter leading-none">Wealth <br /> Composition</h4>
                          <p className="text-slate-400 text-sm font-bold uppercase tracking-[0.1em] leading-relaxed max-w-sm">The compounded yield has generated <span className="text-amber-500">{((parseFloat(animatedSipResult.returns) / parseFloat(animatedSipResult.invested)) * 100).toFixed(1)}%</span> growth over core allocation.</p>
                        </div>
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-4 bg-white/10 p-4 rounded-2xl border border-white/20 w-fit backdrop-blur-xl group-hover:scale-105 transition-transform shadow-xl hover:bg-white/20 group/invest cursor-pointer">
                            <div className="w-4 h-4 rounded-full bg-slate-600 shadow-lg"></div>
                            <span className="text-[11px] font-black uppercase text-white/70 tracking-widest group-hover/invest:text-white transition-colors duration-300">INVESTMENT: ₹{Number(animatedSipResult.invested).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex items-center gap-4 bg-amber-500/10 p-4 rounded-2xl border border-amber-500/20 w-fit backdrop-blur-xl group-hover:scale-105 transition-transform shadow-xl hover:bg-amber-500/20 group/profit cursor-pointer">
                            <div className="w-4 h-4 rounded-full bg-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.6)]"></div>
                            <span className="text-[11px] font-black uppercase text-amber-500 tracking-widest group-hover/profit:text-white transition-colors duration-300">PROFIT YIELD: ₹{Number(animatedSipResult.returns).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-full bg-slate-900/20 border-2 border-dashed border-white/5 rounded-[2.5rem] flex items-center justify-center p-12 text-slate-700 font-black text-sm uppercase tracking-[0.4em] text-center">
                    Initiate Probability Induction <br /> to Map Capital Convergence
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* === MARKETS === */}
          <TabsContent value="markets" className="space-y-12">
            <div className="flex gap-2 p-1.5 bg-white/5 border border-white/10 rounded-xl w-fit shadow-2xl animate-in fade-in slide-in-from-left-6 duration-1000">
              <button onClick={() => setMarketCategory('commodities')} className={`px-6 py-2 rounded-lg text-[10px] font-black tracking-widest uppercase transition-all ${marketCategory === 'commodities' ? 'bg-white/10 text-white shadow-lg' : 'text-slate-500'}`}>Indices & Commodities</button>
              <button onClick={() => setMarketCategory('currencies')} className={`px-6 py-2 rounded-lg text-[10px] font-black tracking-widest uppercase transition-all ${marketCategory === 'currencies' ? 'bg-white/10 text-white shadow-lg' : 'text-slate-500'}`}>Currency Exchange Rates</button>
              <button onClick={() => setMarketCategory('crypto')} className={`px-6 py-2 rounded-lg text-[10px] font-black tracking-widest uppercase transition-all ${marketCategory === 'crypto' ? 'bg-white/10 text-white shadow-lg' : 'text-slate-500'}`}>Cryptocurrencies</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {marketCategory === 'commodities' ? (
                <>
                  {[
                    { id: 'gold', title: 'Institutional Gold', price: marketData.gold?.price * usdInr, history: marketData.history.gold, color: COLORS.gold, icon: <TrendingUp />, change: marketData.gold?.change || 0, currency: '₹' },
                    { id: 'silver', title: 'Industrial Silver', price: marketData.silver?.price * usdInr, history: marketData.history.silver, color: COLORS.silver, icon: <TrendingUp />, change: marketData.silver?.change || 0, currency: '₹' },
                    { id: 'crude', title: 'Global Crude WTI', price: marketData.crude_oil?.price * usdInr, history: marketData.history.crude, color: COLORS.oil, icon: <TrendingDown />, change: marketData.crude_oil?.change || 0, currency: '₹' },
                    { id: 'gas', title: 'Natural Gas Henry', price: marketData.natural_gas?.price * usdInr, history: marketData.history.gas, color: COLORS.gas, icon: <Zap />, change: marketData.natural_gas?.change || 0, currency: '₹' },
                    { id: 'btc', title: 'Bitcoin Protocol', price: marketData.btc?.price, history: marketData.history.btc, color: COLORS.btc, icon: <Sparkles />, change: marketData.btc?.change || 0, currency: '$', inr: true },
                    { id: 'eth', title: 'Ethereum Virtual Machine', price: marketData.eth?.price, history: marketData.history.eth, color: COLORS.eth, icon: <PieChartIcon />, change: marketData.eth?.change || 0, currency: '$', inr: true }
                  ].sort((a, b) => Math.abs(b.change) - Math.abs(a.change)).map((item) => (
                    <TiltCard key={item.id} className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 h-[340px] shadow-3xl">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color: item.color.stop1 }}>{item.title}</h3>
                          <div className="text-2xl font-black text-white mt-1">
                            {item.currency}{item.price?.toLocaleString()}
                          </div>
                          {item.inr && (
                            <div className="text-[10px] font-black text-slate-500/60 mt-0.5 tracking-wider italic">
                              ₹{(item.price * usdInr).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                          )}
                        </div>
                        <div className={`p-3 rounded-2xl border shadow-2xl ${item.change >= 0 ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' : 'bg-rose-400/10 text-rose-400 border-rose-400/20'}`}>
                          {item.change >= 0 ? <TrendingUp size={22} /> : <TrendingDown size={22} />}
                        </div>
                      </div>
                      {renderMiniChart(item.history, item.color.stop1, item.color.stop2, item.currency, item.id.includes('btc') || item.id.includes('eth') ? 1 : 1)}
                      <div className="mt-4 flex justify-between items-center">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${item.change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {item.change >= 0 ? '+' : ''}{item.change?.toFixed(2)}% Performance
                        </span>
                        <div className="text-[8px] font-black text-slate-600 uppercase tracking-[0.2em]">Institutional Feed</div>
                      </div>
                    </TiltCard>
                  ))}
                </>
              ) : marketCategory === 'crypto' ? (
                <div className="col-span-full">
                  {marketData.trending.length === 0 ? (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 px-1">
                        <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                        <p className="text-[10px] font-black text-cyan-500/70 uppercase tracking-[0.3em]">Syncing CoinGecko Trending Feed...</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        {[...Array(10)].map((_, i) => (
                          <div key={i} className="bg-[#0F172A] border border-white/5 rounded-2xl p-5 space-y-4 animate-pulse">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-white/10" />
                              <div className="space-y-1.5 flex-1">
                                <div className="h-2.5 bg-white/10 rounded w-2/3" />
                                <div className="h-2 bg-white/5 rounded w-1/3" />
                              </div>
                            </div>
                            <div className="h-12 bg-black/40 rounded-xl border border-white/5" />
                            <div className="h-2 bg-white/5 rounded w-full" />
                            <div className="h-2 bg-white/5 rounded w-3/4" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                      {marketData.trending.map(coin => (
                        <TiltCard key={coin.id} depth={15} className="bg-[#0F172A] border border-white/5 rounded-2xl p-5 group hover:border-cyan-500/30 transition-all">
                          <div className="flex items-center gap-3 mb-4">
                            <img src={coin.thumb} alt={coin.name} className="w-8 h-8 rounded-full border border-white/10" />
                            <div>
                              <h4 className="text-[11px] font-black text-white uppercase tracking-tight">{coin.name}</h4>
                              <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">{coin.symbol}</p>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="bg-black/40 p-3 rounded-xl border border-white/5">
                              <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest mb-1">Market Rate</p>
                              <div className="text-sm font-black text-white">
                                {!coin.price || coin.price === 'N/A'
                                  ? <span className="text-slate-600">Price N/A</span>
                                  : typeof coin.price === 'string' && (coin.price.includes('$') || coin.price.includes('₹'))
                                    ? coin.price
                                    : `$${Number(coin.price).toLocaleString(undefined, { maximumFractionDigits: 4 })}`
                                }
                              </div>
                            </div>

                            <div className="flex justify-between items-center text-[10px] font-bold">
                              <span className="text-slate-500 uppercase">Cap Rank</span>
                              <span className="text-cyan-400">#{coin.market_cap_rank || 'N/A'}</span>
                            </div>

                            <div className="flex justify-between items-center text-[10px] font-bold">
                              <span className="text-slate-500 uppercase">24h Change</span>
                              <div className={`flex items-center gap-1 ${(coin.change || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {(coin.change || 0) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                <span>{(coin.change || 0).toFixed(2)}%</span>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-white/5">
                            <p className="text-[8px] text-slate-600 font-black uppercase tracking-[0.2em]">CoinGecko Trending</p>
                          </div>
                        </TiltCard>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {marketData.currencies.map(curr => (
                    <TiltCard
                      key={curr.symbol}
                      depth={15}
                      className={`bg-gradient-to-br from-white/10 to-transparent border rounded-2xl p-6 relative overflow-hidden group shadow-shiny transition-all duration-500 ${curr.hotness === 'hot' ? 'border-rose-500/30 hover:shadow-rose-500/20' :
                        curr.hotness === 'medium' ? 'border-orange-500/30 hover:shadow-orange-500/20' :
                          'border-emerald-500/30 hover:shadow-emerald-500/20'
                        }`}
                    >
                      <div className={`absolute -right-8 -top-8 w-32 h-32 blur-[60px] opacity-20 group-hover:opacity-40 transition-all duration-700 ${curr.hotness === 'hot' ? 'bg-rose-400' :
                        curr.hotness === 'medium' ? 'bg-orange-400' :
                          'bg-emerald-400'
                        }`}></div>

                      <div className="relative z-10 flex items-center justify-between">
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.4em] mb-1.5">
                            {curr.baseName || (curr.symbol.slice(0, 3) + " • " + curr.symbol.slice(3))}
                          </p>
                          <h4 className="text-2xl font-black text-white tracking-widest leading-none">₹{curr.price?.toFixed(2)}</h4>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className={`flex items-center gap-2 px-2 py-1 rounded-lg shadow-2xl transition-all duration-500 ${curr.trend === 'up' ? 'bg-emerald-500/10 text-emerald-400 shadow-emerald-500/20' :
                            'bg-rose-500/10 text-rose-400 shadow-rose-500/20'
                            }`}>
                            {curr.trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                            <span className="text-[10px] font-black">{curr.change >= 0 ? '+' : ''}{curr.change?.toFixed(2)}%</span>
                          </div>
                          <span className={`text-[9px] font-extrabold tracking-widest uppercase opacity-60 ${curr.trend === 'up' ? 'text-emerald-400' : 'text-rose-400'
                            }`}>{curr.symbol}</span>
                        </div>
                      </div>

                      <div className="mt-6">
                        <div className="h-1 w-full bg-slate-900 rounded-full overflow-hidden shadow-inner">
                          <div className={`h-full w-[75%] animate-pulse shadow-[0_0_10px_rgba(255,255,255,0.2)] ${curr.hotness === 'hot' ? 'bg-gradient-to-r from-rose-400 to-red-600' :
                            curr.hotness === 'medium' ? 'bg-gradient-to-r from-orange-400 to-amber-600' :
                              'bg-gradient-to-r from-emerald-400 to-teal-600'
                            }`}></div>
                        </div>
                        <div className="flex justify-between mt-2.5 text-[8px] font-black text-slate-600 uppercase tracking-widest">
                          <span className={curr.trend === 'up' ? 'text-emerald-500/70' : 'text-rose-500/70'}>
                            {curr.trend === 'up' ? 'Bullish Shift' : 'Bearish Slide'}
                          </span>
                          <span>Unit: 1.00</span>
                        </div>
                      </div>
                    </TiltCard>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* MODALS */}
        {showPortfolioModal && selectedForPortfolio && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-3xl p-4 animate-in fade-in zoom-in-95 duration-500">
            <TiltCard className="bg-[#020617] border border-white/10 p-6 rounded-[2.5rem] w-full max-w-sm relative shadow-6xl">
              <button onClick={() => setShowPortfolioModal(false)} className="absolute top-6 right-6 text-slate-500 hover:text-white transition-all hover:scale-110"><X size={20} /></button>
              <h3 className="text-xl font-black text-white mb-6 tracking-tighter uppercase opacity-80">Add to Portfolio Asset</h3>
              <div className="space-y-5">
                <div className="p-5 bg-slate-950 rounded-xl border border-white/5 shadow-inner">
                  <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mb-1 opacity-60">Subject Asset</p>
                  <p className="text-sm font-bold text-cyan-400 leading-tight uppercase tracking-tight">{selectedForPortfolio.schemeName}</p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Units</Label>
                    <Input type="number" className="bg-black/50 border-white/10 h-10 rounded-lg px-3 font-black text-base text-white shadow-inner" value={portfolioForm.quantity} onChange={e => setPortfolioForm({ ...portfolioForm, quantity: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">NAV (IND)</Label>
                    <Input type="number" className="bg-black/50 border-white/10 h-10 rounded-lg px-3 font-black text-base text-white shadow-inner" value={portfolioForm.buyPrice} onChange={e => setPortfolioForm({ ...portfolioForm, buyPrice: e.target.value })} />
                  </div>
                </div>
                <Button onClick={handlePortfolioSubmit} className="w-full h-11 bg-cyan-600 hover:bg-cyan-500 text-[10px] font-black uppercase rounded-xl mt-4 tracking-[0.15em] shadow-2xl shadow-cyan-900/40">Confirm Induction</Button>
              </div>
            </TiltCard>
          </div>
        )}

        {showAddModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-3xl p-4 animate-in fade-in zoom-in-95 duration-500">
            <TiltCard className="bg-[#020617] border border-white/10 p-10 rounded-[3rem] w-full max-w-2xl relative shadow-6xl">
              <button onClick={() => setShowAddModal(false)} className="absolute top-8 right-8 text-slate-500 hover:text-white transition-all hover:scale-110"><X size={24} /></button>
              <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-tight">Systematic Asset Query</h3>
              <p className="text-slate-500 text-[10px] font-black uppercase mb-10 tracking-[0.2em]">Institutional Registry Interface</p>

              <div className="space-y-8">
                <div className="relative group">
                  <div className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-cyan-500 transition-colors">
                    <Search size={20} />
                  </div>
                  <Input
                    className="bg-black/50 border-white/5 text-white h-16 rounded-[1.5rem] pl-16 pr-24 text-lg font-black focus:ring-cyan-500 shadow-inner placeholder:text-white/10"
                    placeholder="Search by name or Enter Scheme ID"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGlobalSearch()}
                  />

                  {/* SEARCH BUTTON INSIDE BAR */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <Button
                      onClick={handleGlobalSearch}
                      disabled={searching || !searchCode}
                      className="bg-cyan-600 hover:bg-cyan-500 text-[10px] font-black uppercase px-4 h-10 rounded-xl transition-all shadow-lg active:scale-95 flex items-center gap-2"
                    >
                      {searching ? <RefreshCw className="animate-spin" size={14} /> : <Search size={14} />}
                      Registry
                    </Button>
                  </div>

                  {/* DROPDOWN SUGGESTIONS - SIMULTANEOUS BUT NOT FORCEFUL */}
                  {suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-3 bg-[#0C111D]/90 border border-white/10 rounded-[1.5rem] overflow-hidden z-50 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-3xl animate-in fade-in slide-in-from-top-2 duration-300 max-h-60 overflow-y-auto custom-scrollbar">
                      {suggestions.map((s, idx) => (
                        <button
                          key={idx}
                          onClick={async () => {
                            setSearchCode(s.schemeCode.toString());
                            setSearching(true);
                            const res = await financialDataAPI.getMFDetails(s.schemeCode);
                            setSearchResult(res);
                            setSearching(false);
                            setSuggestions([]);
                          }}
                          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors group/item border-b border-white/5 last:border-0"
                        >
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-xs font-bold text-white group-hover/item:text-cyan-400 transition-colors line-clamp-1">{s.schemeName}</span>
                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">ID: {s.schemeCode}</span>
                          </div>
                          <ChevronRight size={16} className="text-slate-700 group-hover/item:translate-x-1 group-hover/item:text-cyan-500 transition-all" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {searchResult && (
                  <div className="bg-white/2 p-8 rounded-3xl border border-white/10 animate-in zoom-in-95 duration-500 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5"><Zap size={48} /></div>
                    <p className="font-extrabold text-white text-lg mb-2 uppercase tracking-tight leading-tight">{searchResult.schemeName}</p>
                    <p className="text-[9px] text-slate-500 font-black uppercase mb-8 tracking-[0.2em]">{searchResult.fundHouse}</p>
                    <div className="flex items-center justify-between mb-8">
                      <div>
                        <p className="text-[8px] text-slate-500 font-black uppercase mb-1">Live NAV Induction</p>
                        <span className="text-4xl font-black text-cyan-400">₹{searchResult.currentNav}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-slate-500 font-black uppercase mb-1">Last Updated</p>
                        <span className="text-[10px] text-slate-400 font-mono tracking-widest uppercase">{searchResult.date}</span>
                      </div>
                    </div>
                    <Button onClick={() => { addFundToTracking(searchResult); setShowAddModal(false); setSearchResult(null); setSearchCode(''); }} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-[10px] font-black uppercase rounded-xl tracking-[0.1em] shadow-2xl flex items-center justify-center gap-3">
                      Synchronize Tracker <ArrowRight size={16} />
                    </Button>
                  </div>
                )}
                <div className="bg-cyan-500/5 p-4 rounded-2xl border border-cyan-500/10 flex items-center gap-4">
                  <Info size={20} className="text-cyan-500 shrink-0" />
                  <p className="text-[9px] text-cyan-400/80 leading-relaxed font-black uppercase tracking-widest">Technician Note: Direct scheme IDs allow high-speed access to market instruments. Registry lookups take priority.</p>
                </div>
              </div>
            </TiltCard>
          </div>
        )}

      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .font-sans { font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.1); }
        .preserve-3d { transform-style: preserve-3d; }
        .shadow-shiny { box-shadow: 0 0 20px rgba(34, 211, 238, 0.2); }
        .shadow-6xl { box-shadow: 0 50px 100px -20px rgba(0,0,0,1); }
        .animate-spin-slow { animation: spin 8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </DashboardLayout>
  );
};

export default Investments;